// Chat Class - Stores messages and data unique to the Revolt system
// Represents a single participant's perspective/conversation within a group chat
// Supports JSON serialization/deserialization for persistence

import { ChatMessage } from "./ChatMessage.js";

export class Chat {
  static CURRENT_SCHEMA_VERSION = 3;
  static MIN_SUPPORTED_SCHEMA_VERSION = 3;

  constructor() {
    this.messages = new Map(); // Map<UUID, ChatMessage>
    this.messageIDs = []; // Array of UUIDs in order

    // Unique identifier for this chat instance
    this.id = crypto.randomUUID(); // Unique ID for chat identification

    // Metadata for tracking creation and modifications
    const now = Date.now();
    this.createdAt = now;   // Timestamp when chat instance was created
    this.modifiedAt = now;  // Timestamp when chat was last modified
    this.name = "Untitled Chat"; // Human-readable name for the chat

    // Schema versioning (for backward compatibility checks)
    this.schemaVersion = Chat.CURRENT_SCHEMA_VERSION;

    // Default capability metadata - suggestions only (blocking is determined per-section)
    // These are default recommendations, but each section decides its own blocking status
    this.defaultCapabilityMetadata = {
      "Function Calling": { defaultBlocking: true },
      Thinking: { defaultBlocking: false },
      "Structured Outputs": { defaultBlocking: true },
      "Web Search": { defaultBlocking: false },
      "Image Input": { defaultBlocking: true },
      "Image Output": { defaultBlocking: true },
      "Audio Input": { defaultBlocking: true },
      "Video Input": { defaultBlocking: true },
      "File Input": { defaultBlocking: true },
    };

    // Checkpoint tracking (conversation-scoped)
    this.checkpointOrder = []; // Array of checkpoint IDs in chronological order
    this.checkpointsById = {}; // Map-like object keyed by checkpoint ID
    this.redoStash = []; // Array of redo checkpoint IDs
    this.redoById = {}; // Map-like object keyed by redo checkpoint ID

    // Arbitrary chat-instance tool data (per-namespace, optionally per-tool-call).
    // Shape: { [namespace]: any | { byToolCallId: { [toolCallId]: any }, ...other } }
    // This is the "global per-chat data bag" used by tools that mutate state on
    // the chat itself (e.g. todo-write) instead of an external store like the
    // filesystem. Persisted via toJSON/fromJSON and participates in the
    // checkpoint/restore system through tool-defined entries.
    this.instanceToolData = {};
  }

  /**
   * Generate a UUID for a message
   * @returns {string} UUID
   */
  _generateMessageID() {
    return crypto.randomUUID();
  }

  /**
   * Update the modified timestamp to current time
   * @private
   */
  _updateModified() {
    this.modifiedAt = Date.now();
  }

  /**
   * Get the chat ID
   * @returns {string} The unique chat ID
   */
  getId() {
    return this.id;
  }

  /**
   * Set the chat name and update modification timestamp
   * @param {string} name - The new name for the chat
   */
  setName(name) {
    this.name = name || "Untitled Chat";
    this._updateModified();
  }

  /**
   * Get the chat name
   * @returns {string} The chat name
   */
  getName() {
    return this.name;
  }

  /**
   * CREATE: Add a new message to the chat
   * @param {Object} messageData - Data object with role and content
   * @param {number|null} position - Optional position to insert at (default: end)
   * @returns {string} The UUID of the created message
   */
  addMessage(messageData, position = null) {
    const messageID = this._generateMessageID();
    const message = new ChatMessage();

    // Set message metadata (role, name)
    message.data.role = messageData.role || null;
    message.data.name = messageData.name || null;

    // If messageData has segments, use them directly
    if (messageData.segments && Array.isArray(messageData.segments)) {
      message.data.segments = [...messageData.segments];
      message._updateAggregatedCapabilities();
    } else if (
      (typeof messageData.content === "string" && messageData.content.length > 0) ||
      (Array.isArray(messageData.content) && messageData.content.length > 0) ||
      (messageData.content !== null &&
        messageData.content !== undefined &&
        typeof messageData.content !== "string" &&
        !Array.isArray(messageData.content))
    ) {
      // Convert content format to a text segment
      const now = new Date();
      message.addSegment({
        type: "text",
        content: messageData.content,
        blocking: false,
        startTimestamp: now,
        endTimestamp: now,
      });

      // Handle tool_calls, tool_call_id, reasoning
      if (messageData.tool_calls) {
        message.addSegment({
          type: "function_call",
          content: messageData.tool_calls,
          blocking: true,
          startTimestamp: now,
          endTimestamp: now,
        });
      }
      if (messageData.tool_call_id) {
        message.addSegment({
          type: "function_call",
          content: { tool_call_id: messageData.tool_call_id },
          blocking: true,
          startTimestamp: now,
          endTimestamp: now,
        });
      }
      if (messageData.reasoning) {
        message.addSegment({
          type: "reasoning",
          content: messageData.reasoning,
          blocking: false,
          startTimestamp: now,
          endTimestamp: now,
        });
      }
    }

    this.messages.set(messageID, message);

    if (position === null || position >= this.messageIDs.length) {
      // Add to end
      this.messageIDs.push(messageID);
    } else if (position < 0) {
      // Add to beginning
      this.messageIDs.unshift(messageID);
    } else {
      // Insert at specific position
      this.messageIDs.splice(position, 0, messageID);
    }

    // Update modification timestamp
    this._updateModified();

    return messageID;
  }

  /**
   * READ: Get a message by ID
   * @param {string} messageID - UUID of the message
   * @returns {ChatMessage|null} The message or null if not found
   */
  getMessage(messageID) {
    return this.messages.get(messageID) || null;
  }

  /**
   * READ: Get all messages in order
   * @returns {Array<ChatMessage>} Array of messages in chronological order
   */
  getAllMessages() {
    return this.messageIDs
      .map((id) => this.messages.get(id))
      .filter((msg) => msg !== undefined);
  }

  /**
   * READ: Get messages in a range
   * @param {number} startIndex - Start index (inclusive)
   * @param {number} endIndex - End index (exclusive)
   * @returns {Array<ChatMessage>} Array of messages in the range
   */
  getMessagesInRange(startIndex, endIndex) {
    const rangeIDs = this.messageIDs.slice(startIndex, endIndex);
    return rangeIDs
      .map((id) => this.messages.get(id))
      .filter((msg) => msg !== undefined);
  }

  /**
   * READ: Check if a message exists
   * @param {string} messageID - UUID of the message
   * @returns {boolean} True if message exists
   */
  hasMessage(messageID) {
    return this.messages.has(messageID);
  }

  /**
   * READ: Get the total number of messages
   * @returns {number} Message count
   */
  getMessageCount() {
    return this.messageIDs.length;
  }

  /**
   * READ: Get the index of a message in the ordered list
   * @param {string} messageID - UUID of the message
   * @returns {number} Index of the message, or -1 if not found
   */
  getMessageIndex(messageID) {
    return this.messageIDs.indexOf(messageID);
  }

  /**
   * UPDATE: Update an existing message
   * @param {string} messageID - UUID of the message to update
   * @param {Object} messageData - New data object (will merge with existing data)
   * @returns {boolean} True if message was updated, false if not found
   */
  updateMessage(messageID, messageData) {
    const message = this.messages.get(messageID);
    if (!message) {
      return false;
    }

    // Merge the data (excluding segments which have special handling)
    const { segments, ...dataToMerge } = messageData;
    Object.assign(message.data, dataToMerge);

    // If segments are provided, replace them
    if (segments && Array.isArray(segments)) {
      message.data.segments = [...segments];
      message._updateAggregatedCapabilities();
    }

    // Update modification timestamp
    this._updateModified();

    return true;
  }

  /**
   * DELETE: Remove a message by ID
   * @param {string} messageID - UUID of the message to remove
   * @returns {boolean} True if message was removed, false if not found
   */
  removeMessage(messageID) {
    if (!this.messages.has(messageID)) {
      return false;
    }

    this.messages.delete(messageID);
    const index = this.messageIDs.indexOf(messageID);
    if (index !== -1) {
      this.messageIDs.splice(index, 1);
    }

    // Update modification timestamp
    this._updateModified();

    return true;
  }

  /**
   * DELETE: Remove all messages
   */
  clearMessages() {
    this.messages.clear();
    this.messageIDs = [];

    // Update modification timestamp
    this._updateModified();
  }

  // ---------------------------------------------------------------------------
  // Instance tool data bag
  //
  // Tools can keep arbitrary data on the Chat instance, scoped by a string
  // namespace (e.g. "todos") and optionally also by a tool-call id when the
  // tool wants per-invocation storage. Mutations bump the modified timestamp;
  // persistence/notification is driven by AIInferenceSystem.onChatModified.
  // ---------------------------------------------------------------------------

  /**
   * READ: Get the namespace value (or null if missing).
   * @param {string} namespace
   * @returns {*}
   */
  getInstanceToolData(namespace) {
    if (!namespace || typeof namespace !== "string") return null;
    if (!this.instanceToolData || typeof this.instanceToolData !== "object") {
      this.instanceToolData = {};
    }
    return this.instanceToolData[namespace] ?? null;
  }

  /**
   * UPDATE: Set the value for a namespace (replaces any previous value).
   * @param {string} namespace
   * @param {*} value
   * @returns {boolean} true on success
   */
  setInstanceToolData(namespace, value) {
    if (!namespace || typeof namespace !== "string") return false;
    if (!this.instanceToolData || typeof this.instanceToolData !== "object") {
      this.instanceToolData = {};
    }
    this.instanceToolData[namespace] = value;
    this._updateModified();
    return true;
  }

  /**
   * DELETE: Remove a namespace entirely.
   * @param {string} namespace
   * @returns {boolean} true if a value existed and was removed
   */
  deleteInstanceToolData(namespace) {
    if (!namespace || typeof namespace !== "string") return false;
    if (!this.instanceToolData || typeof this.instanceToolData !== "object") {
      this.instanceToolData = {};
      return false;
    }
    if (!Object.prototype.hasOwnProperty.call(this.instanceToolData, namespace)) {
      return false;
    }
    delete this.instanceToolData[namespace];
    this._updateModified();
    return true;
  }

  /**
   * READ: Get tool-call-scoped data within a namespace.
   * Convention: stored at instanceToolData[namespace].byToolCallId[toolCallId].
   * @param {string} namespace
   * @param {string} toolCallId
   * @returns {*}
   */
  getInstanceToolCallData(namespace, toolCallId) {
    if (!namespace || !toolCallId) return null;
    const ns = this.getInstanceToolData(namespace);
    if (!ns || typeof ns !== "object" || Array.isArray(ns)) return null;
    return ns.byToolCallId?.[toolCallId] ?? null;
  }

  /**
   * UPDATE: Set tool-call-scoped data within a namespace.
   * Coerces the namespace value to an object if needed (keeping any
   * existing fields), and writes under byToolCallId[toolCallId].
   * @param {string} namespace
   * @param {string} toolCallId
   * @param {*} value
   * @returns {boolean} true on success
   */
  setInstanceToolCallData(namespace, toolCallId, value) {
    if (!namespace || !toolCallId) return false;
    if (!this.instanceToolData || typeof this.instanceToolData !== "object") {
      this.instanceToolData = {};
    }
    let ns = this.instanceToolData[namespace];
    if (!ns || typeof ns !== "object" || Array.isArray(ns)) {
      ns = {};
      this.instanceToolData[namespace] = ns;
    }
    if (!ns.byToolCallId || typeof ns.byToolCallId !== "object") {
      ns.byToolCallId = {};
    }
    ns.byToolCallId[toolCallId] = value;
    this._updateModified();
    return true;
  }

  /**
   * DELETE: Remove a tool-call-scoped entry within a namespace.
   * @param {string} namespace
   * @param {string} toolCallId
   * @returns {boolean} true if removed
   */
  deleteInstanceToolCallData(namespace, toolCallId) {
    if (!namespace || !toolCallId) return false;
    const ns = this.instanceToolData?.[namespace];
    if (!ns || typeof ns !== "object" || !ns.byToolCallId) return false;
    if (!Object.prototype.hasOwnProperty.call(ns.byToolCallId, toolCallId)) {
      return false;
    }
    delete ns.byToolCallId[toolCallId];
    this._updateModified();
    return true;
  }

  /**
   * Get all capabilities used across all segments in all messages in the chat
   * @returns {Set<string>} Set of all capability strings
   */
  getActiveCapabilities() {
    const allCapabilities = new Set();

    for (const message of this.messages.values()) {
      // Iterate through all segments in the message
      for (const segment of message.data.segments || []) {
        for (const capability of segment.capabilities || []) {
          allCapabilities.add(capability);
        }
      }
    }

    return allCapabilities;
  }

  /**
   * Get all capabilities as an array
   * @returns {string[]} Array of all capability strings
   */
  getActiveCapabilitiesArray() {
    return Array.from(this.getActiveCapabilities());
  }

  /**
   * Get only blocking capabilities used in the chat
   * Blocking capabilities are determined per-segment, not globally
   * @returns {Set<string>} Set of blocking capability strings
   */
  getBlockingCapabilities() {
    const blocking = new Set();

    // Iterate through all messages and their segments
    for (const message of this.messages.values()) {
      // Check each segment - if it's marked as blocking, add its capabilities
      for (const segment of message.data.segments || []) {
        if (segment.blocking === true) {
          for (const capability of segment.capabilities || []) {
            blocking.add(capability);
          }
        }
      }
    }

    return blocking;
  }

  /**
   * Get blocking capabilities as an array
   * @returns {string[]} Array of blocking capability strings
   */
  getBlockingCapabilitiesArray() {
    return Array.from(this.getBlockingCapabilities());
  }

  /**
   * Get only non-blocking capabilities used in the chat
   * Non-blocking capabilities are determined per-segment
   * @returns {Set<string>} Set of non-blocking capability strings
   */
  getNonBlockingCapabilities() {
    const nonBlocking = new Set();

    // Iterate through all messages and their segments
    for (const message of this.messages.values()) {
      // Check each segment - if it's marked as non-blocking, add its capabilities
      for (const segment of message.data.segments || []) {
        if (segment.blocking === false) {
          for (const capability of segment.capabilities || []) {
            nonBlocking.add(capability);
          }
        }
      }
    }

    return nonBlocking;
  }

  /**
   * Get non-blocking capabilities as an array
   * @returns {string[]} Array of non-blocking capability strings
   */
  getNonBlockingCapabilitiesArray() {
    return Array.from(this.getNonBlockingCapabilities());
  }

  /**
   * Check if a specific capability is used in the chat
   * @param {string} capability - Capability name to check
   * @returns {boolean} True if capability is used
   */
  hasCapability(capability) {
    return this.getActiveCapabilities().has(capability);
  }

  /**
   * Check if a specific capability is blocking in this chat
   * @param {string} capability - Capability name to check
   * @returns {boolean} True if capability is used and is blocking
   */
  hasBlockingCapability(capability) {
    return this.getBlockingCapabilities().has(capability);
  }

  /**
   * Get compatible models from a list of available models
   * Filters models to only those that support all blocking capabilities
   * @param {Array<Object>} availableModels - Array of model objects with 'capabilities' or 'detectedCapabilities' property
   * @returns {Array<Object>} Filtered array of compatible models
   */
  getCompatibleModels(availableModels) {
    const blockingCapabilities = this.getBlockingCapabilities();

    // If no blocking capabilities, all models are compatible
    if (blockingCapabilities.size === 0) {
      return availableModels;
    }

    return availableModels.filter((model) => {
      // Get model capabilities (support both 'capabilities' and 'detectedCapabilities' properties)
      const modelCapabilities =
        model.capabilities || model.detectedCapabilities || [];
      const modelCapabilitySet = new Set(
        Array.isArray(modelCapabilities) ? modelCapabilities : []
      );

      // Check if model supports all blocking capabilities
      for (const requiredCapability of blockingCapabilities) {
        if (!modelCapabilitySet.has(requiredCapability)) {
          return false;
        }
      }

      return true;
    });
  }

  /**
   * Update default capability metadata (suggestions only)
   * Note: Blocking status is determined per-segment, not globally
   * @param {string} capability - Capability name
   * @param {Object} metadata - Metadata object with 'defaultBlocking' property
   */
  updateDefaultCapabilityMetadata(capability, metadata) {
    if (!this.defaultCapabilityMetadata[capability]) {
      this.defaultCapabilityMetadata[capability] = {};
    }
    this.defaultCapabilityMetadata[capability] = {
      ...this.defaultCapabilityMetadata[capability],
      ...metadata,
    };

    // Update modification timestamp
    this._updateModified();
  }

  /**
   * Get default blocking suggestion for a capability
   * @param {string} capability - Capability name
   * @returns {boolean|null} Default blocking suggestion, or null if not found
   */
  getDefaultBlockingSuggestion(capability) {
    const metadata = this.defaultCapabilityMetadata[capability];
    return metadata?.defaultBlocking ?? null;
  }

  /**
   * Convert Chat messages to OpenRouter API format
   * @returns {Array} Array of messages in OpenRouter format
   */
  toOpenRouterFormat() {
    return this.getAllMessages().map((message) => {
      // Convert segments to OpenRouter format
      const formatted = message.toOpenRouterFormat();

      const openRouterMessage = {
        role: formatted.role,
        content: formatted.content,
      };

      // Add optional fields if they exist
      if (formatted.name !== null && formatted.name !== undefined) {
        openRouterMessage.name = formatted.name;
      }

      if (formatted.tool_calls !== null && formatted.tool_calls !== undefined) {
        openRouterMessage.tool_calls = formatted.tool_calls;
      }

      if (
        formatted.tool_call_id !== null &&
        formatted.tool_call_id !== undefined
      ) {
        openRouterMessage.tool_call_id = formatted.tool_call_id;
      }

      if (formatted.reasoning !== null && formatted.reasoning !== undefined) {
        openRouterMessage.reasoning = formatted.reasoning;
      }

      return openRouterMessage;
    });
  }

  /**
   * Get messages formatted for OpenRouter API request
   * @param {Object} options - Additional options
   * @param {boolean} options.includeSystem - Include system messages (default: true)
   * @param {number|null} options.maxMessages - Maximum number of messages to include (null = all)
   * @returns {Array} Array of messages in OpenRouter format
   */
  getOpenRouterMessages(options = {}) {
    const { includeSystem = true, maxMessages = null } = options;

    let messages = this.getAllMessages();

    // Filter out system messages if requested
    if (!includeSystem) {
      messages = messages.filter((msg) => msg.data.role !== "system");
    }

    // Remove trailing assistant messages at the end
    let lastNonAssistantIndex = messages.length - 1;
    while (
      lastNonAssistantIndex >= 0 &&
      messages[lastNonAssistantIndex]?.data?.role === "assistant"
    ) {
      lastNonAssistantIndex--;
    }
    // Only slice if we found any trailing assistant messages
    if (lastNonAssistantIndex < messages.length - 1) {
      messages = messages.slice(0, lastNonAssistantIndex + 1);
    }

    // Limit number of messages if specified
    if (maxMessages !== null && maxMessages > 0) {
      messages = messages.slice(-maxMessages);
    }

    return messages.map((message) => {
      // Convert segments to OpenRouter format
      const formatted = message.toOpenRouterFormat();

      const openRouterMessage = {
        role: formatted.role,
        // content can be null for assistant messages with tool_calls (OpenRouter spec)
        // Always include it explicitly, even if null
        content: formatted.content !== undefined ? formatted.content : null,
      };

      // Add optional fields if they exist
      if (formatted.name !== null && formatted.name !== undefined) {
        openRouterMessage.name = formatted.name;
      }

      if (formatted.tool_calls !== null && formatted.tool_calls !== undefined) {
        openRouterMessage.tool_calls = formatted.tool_calls;
      }

      if (
        formatted.tool_call_id !== null &&
        formatted.tool_call_id !== undefined
      ) {
        openRouterMessage.tool_call_id = formatted.tool_call_id;
      }

      if (formatted.reasoning !== null && formatted.reasoning !== undefined) {
        openRouterMessage.reasoning = formatted.reasoning;
      }

      return openRouterMessage;
    });
  }

  /**
   * Extract plain-text content from an OpenRouter-style content payload.
   * node-llama-cpp local chat currently only consumes text content here.
   *
   * TODO (multimodal parity):
   * 1. Detect model capabilities (vision/audio/file input) and choose a serialization strategy per model.
   * 2. Extend local node-llama-cpp prompt/history conversion so `content` can include non-text inputs
   *    without stringifying them into huge blobs.
   * 3. Optionally transform/clamp the persisted chat structure per-model (so cloud + local stay aligned).
   * @param {*} content
   * @returns {string}
   */
  _flattenContentForLlamaCpp(content) {
    if (typeof content === "string") {
      return content;
    }

    if (Array.isArray(content)) {
      // For now, we intentionally drop non-text content (images/audio/files) to keep local mode functional.
      // Later we’ll include these based on model capabilities rather than always flattening to text.
      return content
        .map((item) => {
          if (!item || typeof item !== "object") {
            return "";
          }

          if (item.type === "text" && typeof item.text === "string") {
            return item.text;
          }

          return "";
        })
        .join("");
    }

    if (content == null) {
      return "";
    }

    try {
      return JSON.stringify(content);
    } catch (_) {
      return String(content);
    }
  }

  /**
   * Read the plain-text message body from the Revolt chat structure.
   * @param {ChatMessage} message
   * @returns {string}
   */
  _getMessagePlainText(message) {
    if (!message) {
      return "";
    }

    const formatted = message.toOpenRouterFormat();
    return this._flattenContentForLlamaCpp(formatted?.content);
  }

  /**
   * Parse tool arguments/results from transcript storage into structured values when possible.
   * @param {*} value
   * @returns {*}
   */
  _parseLlamaCppStructuredValue(value) {
    if (typeof value !== "string") {
      return value;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return "";
    }

    try {
      return JSON.parse(trimmed);
    } catch (_) {
      return value;
    }
  }

  /**
   * Get normalized assistant tool calls from message data/segments.
   * @param {ChatMessage} message
   * @returns {Array<Object>}
   */
  _getAssistantToolCalls(message) {
    if (!message) {
      return [];
    }

    const formatted = message.toOpenRouterFormat();
    const toolCalls = Array.isArray(formatted?.tool_calls)
      ? formatted.tool_calls
      : [];

    return toolCalls
      .map((toolCall, index) => {
        const functionName = toolCall?.function?.name;
        if (!functionName) {
          return null;
        }

        return {
          id: toolCall?.id || `tool-call-${index}`,
          name: functionName,
          params: this._parseLlamaCppStructuredValue(
            toolCall?.function?.arguments ?? {}
          ),
        };
      })
      .filter(Boolean);
  }

  /**
   * Collect tool result messages that immediately follow an assistant tool-call turn.
   * @param {Array<ChatMessage>} messages
   * @param {number} startIndex
   * @returns {{ resultsById: Map<string, *>, consumedCount: number }}
   */
  _collectFollowingToolResults(messages, startIndex) {
    const resultsById = new Map();
    let consumedCount = 0;

    for (let index = startIndex; index < messages.length; index += 1) {
      const message = messages[index];
      if (message?.data?.role !== "tool") {
        break;
      }

      const formatted = message.toOpenRouterFormat();
      const toolCallId =
        typeof formatted?.tool_call_id === "string"
          ? formatted.tool_call_id
          : null;
      const resultValue = this._parseLlamaCppStructuredValue(formatted?.content);

      if (toolCallId) {
        resultsById.set(toolCallId, resultValue);
      }

      consumedCount += 1;
    }

    return { resultsById, consumedCount };
  }

  /**
   * Convert assistant segments/tool calls into node-llama-cpp model response entries.
   * Tool calls are split into sequential model turns because local mode executes one tool at a time.
   * @param {ChatMessage} message
   * @param {Map<string, *>} resultsById
   * @returns {Array<Object>}
   */
  _getAssistantLlamaCppResponses(message, resultsById = new Map()) {
    const responsePieces = [];
    const orderedToolCalls = [];
    const orderedToolCallIds = new Set();
    const assistantToolCalls = this._getAssistantToolCalls(message);
    const toolCallMap = new Map(
      assistantToolCalls.map((toolCall) => [toolCall.id, toolCall])
    );

    for (const segment of message?.data?.segments || []) {
      if (segment?.type === "text" && typeof segment.content === "string") {
        responsePieces.push(segment.content);
        continue;
      }

      if (
        (segment?.type === "reasoning" || segment?.type === "thinking") &&
        typeof segment.content === "string"
      ) {
        responsePieces.push({
          type: "segment",
          segmentType: "thought",
          text: segment.content,
        });
        continue;
      }

      if (segment?.type === "tool_call") {
        const toolCallId = segment.toolCallId || segment.content?.toolCallId;
        const fallbackTool = toolCallId ? toolCallMap.get(toolCallId) : null;
        const functionName =
          segment.functionName ||
          segment.content?.functionName ||
          fallbackTool?.name;
        const params =
          segment.arguments ??
          segment.content?.arguments ??
          fallbackTool?.params ??
          {};

        const normalizedId =
          toolCallId ||
          fallbackTool?.id ||
          `tool-call-${orderedToolCalls.length}`;

        if (!orderedToolCallIds.has(normalizedId) && functionName) {
          orderedToolCalls.push({
            id: normalizedId,
            name: functionName,
            params,
          });
          orderedToolCallIds.add(normalizedId);
        }
      }
    }

    for (const toolCall of assistantToolCalls) {
      if (!orderedToolCallIds.has(toolCall.id)) {
        orderedToolCalls.push(toolCall);
        orderedToolCallIds.add(toolCall.id);
      }
    }

    if (orderedToolCalls.length === 0) {
      return responsePieces.length > 0
        ? [{ type: "model", response: responsePieces }]
        : [];
    }

    const responses = [];
    orderedToolCalls.forEach((toolCall, index) => {
      const pieces = index === 0 ? [...responsePieces] : [];
      pieces.push({
        type: "functionCall",
        name: toolCall.name,
        params: toolCall.params,
        result: resultsById.has(toolCall.id) ? resultsById.get(toolCall.id) : null,
      });
      responses.push({
        type: "model",
        response: pieces,
      });
    });

    return responses;
  }

  /**
   * Get messages formatted for node-llama-cpp chat/session history.
   * Assistant tool calls are rewritten into sequential model functionCall responses with embedded results.
   * @param {Object} options
   * @param {boolean} options.includeSystem - Include system messages (default: true)
   * @param {number|null} options.maxMessages - Maximum source messages to include (null = all)
   * @returns {Array<Object>}
   */
  getNodeLlamaCppMessages(options = {}) {
    const { includeSystem = true, maxMessages = null } = options;

    let messages = this.getAllMessages();

    if (!includeSystem) {
      messages = messages.filter((msg) => msg.data.role !== "system");
    }

    let lastNonAssistantIndex = messages.length - 1;
    while (
      lastNonAssistantIndex >= 0 &&
      messages[lastNonAssistantIndex]?.data?.role === "assistant"
    ) {
      lastNonAssistantIndex--;
    }
    if (lastNonAssistantIndex < messages.length - 1) {
      messages = messages.slice(0, lastNonAssistantIndex + 1);
    }

    if (maxMessages !== null && maxMessages > 0) {
      messages = messages.slice(-maxMessages);
    }

    const history = [];

    for (let index = 0; index < messages.length; index += 1) {
      const message = messages[index];
      const role = message?.data?.role;

      if (role === "tool") {
        continue;
      }

      if (role === "system") {
        // TODO (multimodal parity):
        // If/when the local node-llama-cpp wrapper supports non-text system inputs,
        // ensure we pass them through without losing fidelity.
        history.push({
          type: "system",
          text: this._getMessagePlainText(message),
        });
        continue;
      }

      if (role === "user") {
        // TODO (multimodal parity):
        // For models that support image/audio/video/file inputs, preserve these
        // as structured inputs rather than flattening to text.
        history.push({
          type: "user",
          text: this._getMessagePlainText(message),
        });
        continue;
      }

      if (role === "assistant") {
        // TODO (model capability-aware encoding):
        // If a local model supports richer assistant outputs (e.g. tool results displayed as
        // structured content, or multimodal outputs), extend conversion here.
        const { resultsById, consumedCount } = this._collectFollowingToolResults(
          messages,
          index + 1
        );

        const assistantResponses = this._getAssistantLlamaCppResponses(
          message,
          resultsById
        );

        history.push(...assistantResponses);
        index += consumedCount;
      }
    }

    return history;
  }

  /**
   * Convert the chat into the prompt/history shape consumed by the local node-llama-cpp bridge.
   * The final user turn becomes the prompt; earlier turns remain in history.
   * @param {Object} options
   * @param {boolean} options.includeSystem - Include system messages (default: true)
   * @param {number|null} options.maxMessages - Maximum source messages to include (null = all)
   * @returns {{ systemPrompt: string, prompt: string, history: Array<Object> }}
   */
  toNodeLlamaCppFormat(options = {}) {
    const llamaMessages = this.getNodeLlamaCppMessages(options);
    const messages = Array.isArray(llamaMessages) ? [...llamaMessages] : [];

    let prompt = "";
    if (messages.length > 0 && messages[messages.length - 1]?.type === "user") {
      prompt = typeof messages[messages.length - 1]?.text === "string"
        ? messages[messages.length - 1].text
        : "";
      messages.pop();
    }

    let systemPrompt = "";
    const history = [];

    for (const message of messages) {
      if (message?.type === "system") {
        if (!systemPrompt && typeof message.text === "string") {
          systemPrompt = message.text;
        } else {
          history.push(message);
        }
        continue;
      }

      history.push(message);
    }

    return {
      systemPrompt,
      prompt,
      history,
    };
  }

  // Intentionally no aliases: the canonical conversion helper is `toNodeLlamaCppFormat()`.

  /**
   * Clone the entire chat instance with the same message IDs
   * Creates a deep copy with no shared references to the original
   * @returns {Chat} A new Chat instance with cloned messages
   */
  clone() {
    const clonedChat = new Chat();

    // Generate new ID for cloned chat (clones should be separate instances)
    // Preserve original timestamps and name (don't use constructor defaults)
    clonedChat.createdAt = this.createdAt;
    clonedChat.modifiedAt = this.modifiedAt;
    clonedChat.name = this.name;

    // Deep clone defaultCapabilityMetadata
    clonedChat.defaultCapabilityMetadata = JSON.parse(
      JSON.stringify(this.defaultCapabilityMetadata)
    );

    // Deep clone instanceToolData so the clone owns its own bag.
    clonedChat.instanceToolData =
      this.instanceToolData && typeof this.instanceToolData === "object"
        ? this._deepCloneValue(this.instanceToolData)
        : {};

    // Clone all messages with the same IDs
    for (const messageID of this.messageIDs) {
      const originalMessage = this.messages.get(messageID);
      if (!originalMessage) continue;

      // Create new ChatMessage instance
      const clonedMessage = new ChatMessage();

      // Clone message metadata
      clonedMessage.data.role = originalMessage.data.role;
      clonedMessage.data.name = originalMessage.data.name;

      // Deep clone all segments
      clonedMessage.data.segments = originalMessage.data.segments.map(
        (segment) => {
          const clonedSegment = {
            type: segment.type,
            content: this._deepCloneValue(segment.content),
            capabilities: segment.capabilities ? [...segment.capabilities] : [],
            blocking: segment.blocking,
            startTimestamp:
              segment.startTimestamp instanceof Date
                ? new Date(segment.startTimestamp.getTime())
                : segment.startTimestamp,
            endTimestamp:
              segment.endTimestamp instanceof Date
                ? new Date(segment.endTimestamp.getTime())
                : segment.endTimestamp,
            formats: segment.formats
              ? this._deepCloneValue(segment.formats)
              : {},
            activeFormat: segment.activeFormat,
          };
          return clonedSegment;
        }
      );

      // Update aggregated capabilities
      clonedMessage._updateAggregatedCapabilities();

      // Add to cloned chat with the same message ID
      clonedChat.messages.set(messageID, clonedMessage);
      clonedChat.messageIDs.push(messageID);
    }

    return clonedChat;
  }

  /**
   * Deep clone a value (handles objects, arrays, primitives, Date objects)
   * @private
   * @param {*} value - Value to clone
   * @returns {*} Cloned value
   */
  _deepCloneValue(value) {
    if (value === null || value === undefined) {
      return value;
    }

    if (value instanceof Date) {
      return new Date(value.getTime());
    }

    if (Array.isArray(value)) {
      return value.map((item) => this._deepCloneValue(item));
    }

    if (typeof value === "object") {
      const cloned = {};
      for (const key in value) {
        if (value.hasOwnProperty(key)) {
          cloned[key] = this._deepCloneValue(value[key]);
        }
      }
      return cloned;
    }

    // Primitive values (string, number, boolean)
    return value;
  }

  /**
   * Serialize the Chat to a JSON-compatible object
   * @returns {Object} JSON-serializable representation
   */
  toJSON() {
    // Convert Map to serializable object
    const serializable = {
      id: this.id,
      schemaVersion: this.schemaVersion,
      messageIDs: [...this.messageIDs],
      messages: {},
      defaultCapabilityMetadata: this.defaultCapabilityMetadata,
      createdAt: this.createdAt,
      modifiedAt: this.modifiedAt,
      name: this.name,
      checkpointOrder: Array.isArray(this.checkpointOrder)
        ? [...this.checkpointOrder]
        : [],
      checkpointsById: this.checkpointsById || {},
      redoStash: Array.isArray(this.redoStash) ? [...this.redoStash] : [],
      redoById: this.redoById || {},
      instanceToolData:
        this.instanceToolData && typeof this.instanceToolData === "object"
          ? this._deepCloneValue(this.instanceToolData)
          : {},
    };

    // Convert Map to plain object, using toJSON on each message
    for (const [id, message] of this.messages) {
      serializable.messages[id] = message.toJSON();
    }

    return serializable;
  }

  /**
   * Deserialize JSON data and update this Chat instance
   * Clears current data and replaces with new data
   * @param {Object} jsonData - JSON data from toJSON()
   */
  fromJSON(jsonData) {
    // Clear current data
    this.messages.clear();
    this.messageIDs = [];
    this.defaultCapabilityMetadata = {};

    // Restore unique ID (preserve existing or generate new)
    this.id = jsonData.id || crypto.randomUUID();

    // Restore metadata
    this.defaultCapabilityMetadata = jsonData.defaultCapabilityMetadata || {};
    this.createdAt = jsonData.createdAt || Date.now();
    this.modifiedAt = jsonData.modifiedAt || Date.now();
    this.name = jsonData.name || "Untitled Chat";
    this.schemaVersion =
      typeof jsonData.schemaVersion === "number" ? jsonData.schemaVersion : 0;
    this.checkpointOrder = Array.isArray(jsonData.checkpointOrder)
      ? [...jsonData.checkpointOrder]
      : [];
    this.checkpointsById =
      jsonData.checkpointsById && typeof jsonData.checkpointsById === "object"
        ? jsonData.checkpointsById
        : {};
    this.redoStash = Array.isArray(jsonData.redoStash)
      ? [...jsonData.redoStash]
      : [];
    this.redoById =
      jsonData.redoById && typeof jsonData.redoById === "object"
        ? jsonData.redoById
        : {};
    this.instanceToolData =
      jsonData.instanceToolData && typeof jsonData.instanceToolData === "object"
        ? this._deepCloneValue(jsonData.instanceToolData)
        : {};

    // Restore messages
    this.messageIDs = [...(jsonData.messageIDs || [])];

    for (const [id, messageData] of Object.entries(jsonData.messages || {})) {
      const message = new ChatMessage();
      message.fromJSON(messageData);
      this.messages.set(id, message);
    }
  }
}
